/**
 * @fileoverview Order notes display component for showing condensed order note information.
 * Renders order notes in a compact format with author names, timestamps, and editing indicators.
 * Integrates with real-time collaboration features to show current editors.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable eqeqeq */
import React, { useEffect, useState } from 'react';
import { useRecordContext } from 'react-admin';
import { dayjs } from '../../../../components/ex_dayjs';
import { Grid, Tooltip } from '@mui/material';
import { editColor } from '../../../../utils/enum';
import { IData } from '../../../../types';

/**
 * Order notes component for displaying note information with collaboration indicators.
 * Shows condensed view of order notes with author names, timestamps, and visual indicators
 * for users currently editing the order.
 *
 * @param {Object} props - Component properties
 * @param {Array} props.editingOrders - Array of orders currently being edited by users
 * @param {Object} props.vendor - Vendor configuration object with utility methods
 * @param {Function} props.vendor.prettifyAuthorName - Function to format author names
 * @param {boolean} props.usesStrOrderNumber - Whether to use string-based order numbers
 * @returns {JSX.Element} Formatted order notes display with collaboration indicators
 */
const OrderNotes: React.FC<IData> = ({ editingOrders, vendor, usesStrOrderNumber }) => {
    const record = useRecordContext();
    const [rowHeight, setRowHeight] = useState<number | undefined>(0);
    if (!record || record.id === 0) return null;

    let editOrders = editingOrders.filter((x: any) => x.orderNumber == record.order_number || x.orderNumber?.order_number == record.order_number).sort((a: any, b: any) => {
        // Sort by timestamp, most recent first
        return Number(new Date(b.timeStamp)) - Number(new Date(a.timeStamp));
    });
    let editOrder = editOrders.length > 0 ? editOrders[0] : null;

    record.order_notes = record.order_notes || [];
    let title = record.order_notes?.reduce((final: any, note: any) => {
        let item = `${(vendor.prettifyAuthorName(note.author))}: ${note.post} - ${dayjs.utc(note.utc_posted_on).fromNow()}`;
        final.push(item);
        return final;
    }, []);

    const getRowHeight = (record: any) => {
        let orderNumber = usesStrOrderNumber ? record?.str_order_number : record?.order_number;
        // Find the row height based on the order number or str_order_number
        let rowHeight = [...document.querySelectorAll<HTMLElement>(`td.column-${usesStrOrderNumber ? 'str_order_number' : 'order_number'}`)]
            .find(x => x.innerText == orderNumber)?.offsetHeight;
        return rowHeight;
    };

    useEffect(() => {
        if (editOrder) {
            let _rowheight = getRowHeight(record);
            setRowHeight(_rowheight);
            // console.log(`order ${record.order_number} , row height = ${_rowheight}`);
        }
    });//re-calculate row-height after every render in grid: data change makes row height changes

    return (
        <Grid container spacing={1}>
            <Grid sx={{ overflow: 'hidden' }}>
                {title && <Tooltip title={title.join('. ')}>
                    <span className="orderNotes">
                        {record.order_notes.map((note: any, index: number) => {
                            return <span key={`quicknote-${record.order_number}-${index}`}>
                                <span>
                                    <b>{(vendor.prettifyAuthorName(note.author))}: </b>
                                    {note.post} - {dayjs.utc(note.utc_posted_on).fromNow()}
                                </span>
                                <br />
                            </span>;
                        })}
                    </span>
                </Tooltip>}
            </Grid>
            {editOrder && <Grid size={1} >
                <div className="highlight-by" style={{ background: `${editColor[editOrder.boxData.Color - 1]}`, width: rowHeight }}>
                    {editOrder.boxData.By}
                </div>
            </Grid>}


        </Grid>

    );
};
export default OrderNotes;